/**
 * cp_kyc_case models — the governed KYC decision loop.
 * See docs/superpowers/specs/2026-07-03-cp-kyc-case-design.md.
 */

/** `cp_task.type` literal that marks a task as a KYC case. */
export const KYC_CASE_TASK_TYPE = 'kyc_case';

/** The terminal `cp_task_status.label` for a KYC case; everything else is still open. */
export const KYC_STATUS_RESOLVED = 'Resolved';

/** Which open KYC case a review will be recorded against. */
export interface KycCaseTaskMatch {
  taskId: string;
  /** true when this task was opened for the very check being reviewed. */
  exactCheck: boolean;
  /** The check the case was actually opened on — differs when `exactCheck` is false. */
  caseKycCheckId: string | null;
}

/** The disposition types recorded on an append-only `cp_kyc_decision`. */
export type KycDecisionType =
  | 'MANUAL_APPROVE'
  | 'MANUAL_REJECT'
  | 'OVERRIDE_FAIL'
  | 'GRANT_RETRY'
  | 'FLAGGED_FOR_REVIEW';

export type KycResolverType = 'HUMAN' | 'AUTOMATED';

/** Criterion code cited when a case clears because the client's registered data was corrected. */
export const CRITERION_PII_CORRECTED_RECHECK_PASSED = 'PII_CORRECTED_RECHECK_PASSED';

/**
 * What applying a criterion does to the case (`cp_kyc_criteria.outcome`). The agent picks the
 * criterion; the decision type follows from this, never from a separate choice.
 */
export type KycCriteriaOutcome = 'APPROVE' | 'REPROVE' | 'INCONCLUSIVE';

/** `cp_kyc_criteria.outcome` → `cp_kyc_decision.decision_type`. */
export const OUTCOME_TO_DECISION_TYPE: Record<KycCriteriaOutcome, KycDecisionType> = {
  APPROVE: 'MANUAL_APPROVE',
  REPROVE: 'MANUAL_REJECT',
  INCONCLUSIVE: 'FLAGGED_FOR_REVIEW',
};

/** How a `params_schema` field is rendered and where its value comes from. */
export type KycCriteriaParamType = 'text' | 'textarea' | 'boolean' | 'date' | 'number';

/**
 * One fact a criterion requires before it may be applied (`cp_kyc_criteria.params_schema`).
 * `source` is a dot-path resolved against the case context — `pii.*` (with the Personal-data
 * tab's corrections applied), `check.*` (facts off the failing check), `recheck.*` (the
 * re-check outcome). No `source` means the agent supplies the value.
 */
export interface KycCriteriaParamField {
  key: string;
  label: string;
  type: KycCriteriaParamType;
  source?: string | null;
  required?: boolean;
  help?: string | null;
}

export interface KycCriteriaParamsSchema {
  fields: KycCriteriaParamField[];
}

/** A named, strict pass-criterion from the `cp_kyc_criteria` catalog. */
export interface KycCriteria {
  id: string;
  created_at?: string;
  updated_at?: string;
  /** Stable machine code, e.g. `NAME_MATCH_NORMALIZED`. */
  code: string;
  title: string;
  /** The strict definition an agent reads to confirm the case truly matches. */
  description: string;
  /** Which failure class it covers (e.g. `NAME_MISMATCH`). */
  applies_to?: string | null;
  automatable: boolean;
  evaluator_ref?: string | null;
  active: boolean;
  created_by?: string | null;
  origin_decision_id?: string | null;
  /** Declares the facts required to apply this criterion; null = no params block. */
  params_schema?: KycCriteriaParamsSchema | null;
  /** What applying it does. Absent on rows written before the column existed => APPROVE. */
  outcome?: KycCriteriaOutcome | null;
}

/** An immutable audit row from the append-only `cp_kyc_decision` table. */
export interface KycDecision {
  id: string;
  created_at: string;
  task_id?: string | null;
  kyc_check_id?: string | null;
  resulting_kyc_check_id?: string | null;
  checked_user_id?: string | null;
  resolver_type: KycResolverType;
  agent_id?: string | null;
  agent_role?: string | null;
  criteria_id?: string | null;
  criteria_snapshot?: Record<string, unknown> | null;
  criteria_params?: Record<string, unknown> | null;
  decision_type: KycDecisionType;
  reason_code?: string | null;
  justification: string;
  before_state?: { stage?: string; passed?: boolean } | null;
  after_state?: { stage?: string; passed?: boolean } | null;
  /** Joined from cp_user via agent_id, so the log can name who decided. */
  agent?: { display_name?: string | null } | null;
}

/** The resolved `params_schema` values frozen onto a decision. */
export type KycCriteriaParams = Record<string, string | number | boolean | null>;

/** Payload for `POST /kyc-case/apply-criterion` (MANUAL_APPROVE). */
export interface ApplyCriterionPayload {
  task_id?: string;
  kyc_check_id: string;
  checked_user_id: string;
  criteria_id: string;
  justification?: string;
  criteria_params?: KycCriteriaParams;
  /** A passing re-check to adopt as the outcome instead of minting a clearing check. */
  resulting_kyc_check_id?: string;
}

/** Payload for `POST /kyc-case/flag` (FLAGGED_FOR_REVIEW). */
export interface FlagForReviewPayload {
  task_id?: string;
  kyc_check_id: string;
  checked_user_id: string;
  /** The agent's proposed rationale for a future criterion. */
  justification: string;
  criteria_params?: KycCriteriaParams;
}

/** Payload for `POST /kyc-case/reject` (MANUAL_REJECT). */
export interface RejectCasePayload {
  task_id?: string;
  kyc_check_id: string;
  checked_user_id: string;
  reason_code?: string;
  justification: string;
  criteria_id?: string;
  criteria_params?: KycCriteriaParams;
}

/** `cp_kyc_check.stage` values the provider no longer moves away from. */
export const KYC_TERMINAL_STAGES = ['DONE', 'ERROR', 'CANCELLED', 'RESUBMISSION'];

/**
 * A front(+back) document pair already on file from an earlier KYC attempt, offered for reuse
 * so a re-verification does not require the client to capture again.
 */
export interface KycDocumentSet {
  sourceCheckId: string;
  provider: string | null;
  sourceCheckPassed: boolean | null;
  sourceCheckCreatedAt: string | null;
  documentType: string | null;
  frontId: string;
  backId: string | null;
}

/** One field an agent corrected on the Personal-data tab. */
export interface PiiCorrection {
  field: 'legal_name' | 'date_of_birth' | 'id_document_value';
  /** i18n key for the field's display label. */
  labelKey: string;
  before: string;
  after: string;
}

/** Generic response from a decision endpoint. */
export interface KycCaseDecisionResult {
  message?: string;
  decision?: KycDecision;
  [key: string]: unknown;
}
